/* angular modules */
import {Injectable} from '@angular/core';
import {SessionStorage} from 'ngx-webstorage';
import {PanelOptionWrapper} from '../../configuration/shared/model/panelOptionWrapper';
import {OptionsPanelService} from '../../configuration/shared/services/options-panel.service';
import {Item, Project} from '../../project/shared/project-model';
import {UtilService} from '../util/util.service';
import {svgAsPngUri} from 'save-svg-as-png';

import * as domtoimage from 'dom-to-image-more';
import JsPDF from 'jspdf';
import {Layout} from "../../configuration/shared/model/IProduct";
import {SwitchBoardComponent} from "../../configuration/shared/model/component";
import {ComponentType} from "../../configuration/shared/model/component-type";

@Injectable()
export class ExportSldLayoutService {

  @SessionStorage()
  currentItemct: Item;

  @SessionStorage()
  currentProject: Project;

  // Scale/multiplier to scale up the canvas before rendering to reduce fuzzy images
  scaleFactor = 4.5;

  constructor(private utilService: UtilService,
              private optionsPanelService: OptionsPanelService) {
  }

  /**
   * Create and download a PNG image of the currently displayed SLD
   */
  public exportSldAsPng(): Promise<any> {
    // Construct the filename of the generated image
    const filename = this.currentProject.name.toLowerCase().replace(/[^a-z0-9]/, '_') +
      '-' + this.currentItemct.name.toLowerCase().replace(/[^a-z0-9]/, '_') +
      '-sld.png';

    return new Promise((resolve) => {
      const producePng = () => this.produceSldAsPng(domtoimage.toBlob)
        .then(blob => resolve(UtilService.downloadDocument(blob, filename)));

      // Deselect cubicle before converting the SLD to image
      this.deselectSldCubicles();

      // This is a hack to make sure that no cubicle is selected
      setTimeout(producePng, 0);
    });
  }

  /**
   * Create and download a PDF document of the currently displayed SLD
   */
  public exportSldAsPdf(): Promise<any> {
    const filename = this.currentProject.name.toLowerCase().replace(/[^a-z0-9]/, '_') + '-' +
      this.currentItemct.name.toLowerCase().replace(/[^a-z0-9]/, '_') + '-sld.pdf';

    return new Promise((resolve) => {
      const producePdf = () => this.produceSldAsPng(domtoimage.toPng)
        .then(pngDataUrl => resolve(this.pngToPdf(pngDataUrl, filename)));

      // Deselect cubicle before converting the SLD to image
      this.deselectSldCubicles();

      // This is a hack to make sure that no cubicle is selected
      setTimeout(producePdf, 0);
    });
  }

  /**
   * Create and download a PNG image of the currently displayed Layout
   */
  public exportLayoutAsPng(): Promise<any> {
    // Find the dom element containing the layout
    const layoutDomElement = document.getElementById('layout-components-wrapper');

    // Construct the filename of the generated image
    const pngFilename = this.currentProject.name.toLowerCase().replace(/[^a-z0-9]/, '_') + '-' +
      this.currentItemct.name.toLowerCase().replace(/[^a-z0-9]/, '_') + '-layout.png';

    return domtoimage.toBlob(layoutDomElement, {bgcolor: '#fff', scale: this.scaleFactor})
      .then(blob => UtilService.downloadDocument(blob, pngFilename));
  }

  /**
   * Create and download a PDF document of the currently displayed Layout
   */
  public exportLayoutAsPdf(): Promise<any> {
    // Find the dom element containing the layout
    const layoutDomElement = document.getElementById('layout-components-wrapper');

    // Construct the filename of the generated image
    const pdfFilename = this.currentProject.name.toLowerCase().replace(/[^a-z0-9]/, '_') + '-' +
      this.currentItemct.name.toLowerCase().replace(/[^a-z0-9]/, '_') + '-layout.pdf';

    return domtoimage.toPng(layoutDomElement, {scale: this.scaleFactor})
      .then(pngDataUrl => this.pngToPdf(pngDataUrl, pdfFilename));
  }

  /**
   * Deselect any selected cubicle (if any) in the SLD view so that
   * the selection border is not present when we generate the image
   */
  private deselectSldCubicles() {
    this.optionsPanelService.setSelectedComponent(null);
    this.optionsPanelService.updateSelectedComponentInfos(new PanelOptionWrapper(null, null, null));
    this.currentItemct.selectedComponentIndex = -1;
  }

  /**
   * Helper function that removes the selection style, filters the Add and
   * Info buttons, then generate a PNG image of the SLD
   *
   * @param domtoimageFunction Either domtoimage.toPng or domtoimage.toBlob
   */
  private produceSldAsPng(domtoimageFunction): any {
    // Find the dom element containing the SLD
    const sldDomElement = document.getElementById('sld-components-wrapper');

    // Create filter function to remove buttons and overlays from the DOM before any conversion
    const filterButtonsFromSld = node => {
      return !(node && node.tagName && node.tagName.toLowerCase() === 'button') &&
        !(node && node.getAttribute && node.getAttribute('type') === 'button') &&
        !(node && node.classList && node.classList.contains('overlay'));
    };

    const producePng = () => domtoimageFunction(sldDomElement, {
      filter: filterButtonsFromSld,
      bgcolor: '#fff',
      scale: this.scaleFactor
    });
    return producePng();
  }

  /**
   * Take a PNG in dataUrl format, insert it in a PDF with the correct orientation, then make it downloadable.
   *
   * @param pngDataUrl The PNG as a data url
   * @param filename The proposed name for the download
   */
  private pngToPdf(pngDataUrl, filename): Promise<any> {
    let resolvePdfAddImage;
    const pngToPdfPromise = new Promise((resolve => resolvePdfAddImage = resolve));

    // Create an image object in order to determine the width and height of the png blob
    const pngImage = new Image();

    // Use the image onLoad callback to get width and height only after the image is loaded
    pngImage.onload = () => {
      let pngWidth = pngImage.width;
      let pngHeight = pngImage.height;
      const pngRatio = pngWidth / pngHeight;

      const pdf = new JsPDF({orientation: pngWidth > pngHeight ? 'landscape' : 'portrait', unit: 'px', format: 'a4'});
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Compute the destination image size in the pdf in order to maximize used space
      if (pngWidth > pngHeight) {
        // Landscape mode
        pngWidth = pdfWidth;
        pngHeight = pngWidth / pngRatio;
        if (pngHeight > pdfHeight) {
          pngHeight = pdfHeight;
          pngWidth = pngRatio * pngHeight;
        }
      } else {
        // Portrait mode
        pngHeight = pdfHeight;
        pngWidth = pngRatio * pngHeight;
        if (pngWidth > pdfWidth) {
          pngWidth = pdfWidth;
          pngHeight = pngWidth / pngRatio;
        }
      }

      // Add the image to the pdf
      pdf.addImage(pngDataUrl, 'PNG', (pdfWidth - pngWidth) / 2, (pdfHeight - pngHeight) / 2, pngWidth, pngHeight);
      const pdfBlob = pdf.output('blob');

      // Open the download window on the user browser
      UtilService.downloadDocument(pdfBlob, filename);

      resolvePdfAddImage();
    };

    // Set the image source to trigger the onLoad callback
    pngImage.src = pngDataUrl;

    return pngToPdfPromise;
  }

  /**
   * Fetches an SVG file from the given path and converts it to a PNG (Base64 encoded string).
   *
   * @param imagePath - Path to the SVG file.
   * @returns A promise that resolves to a Base64-encoded PNG string.
   */
  public async fetchAndConvertToPng(imagePath: string): Promise<string> {
    const response = await fetch(imagePath);
    const svgText = await response.text();
    return this.convertSvgToPng(svgText);
  }

  /**
   * Converts an SVG string into a PNG image (Base64 encoded).
   * Uses a canvas by default. As a safe fallback, if canvas rendering fails
   * (e.g., SVG contains external <image> tags or certain transforms), it falls back to svgAsPngUri.
   *
   * @param svgText - The raw SVG content as a string.
   * @param scale - The scaling factor for the PNG output. Default is 3.
   * @returns A promise that resolves to a Base64-encoded PNG string.
   */
  private convertSvgToPng(svgText: string, scale: number = 3): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const parser = new DOMParser();
        const svgElement = parser.parseFromString(svgText, 'image/svg+xml').documentElement as unknown as SVGSVGElement;

        if (!svgElement) {
          return reject(new Error('No <svg> element found.'));
        }

        const parseSvgLength = (attr: string | null): number | null => {
          if (!attr || attr.includes('%')) return null;
          const mm = attr.match(/^([\d.]+)\s*mm$/i);
          if (mm) return parseFloat(mm[1]) * (96 / 25.4);
          const cm = attr.match(/^([\d.]+)\s*cm$/i);
          if (cm) return parseFloat(cm[1]) * (96 / 2.54);
          const inch = attr.match(/^([\d.]+)\s*in$/i);
          if (inch) return parseFloat(inch[1]) * 96;
          const n = parseFloat(attr);
          return isNaN(n) ? null : n;
        };

        const viewBoxAttr = svgElement.getAttribute('viewBox');
        let width = parseSvgLength(svgElement.getAttribute('width'));
        let height = parseSvgLength(svgElement.getAttribute('height'));

        if (!width || !height) {
          if (viewBoxAttr) {
            const vb = viewBoxAttr.split(' ').map(Number);
            if (!width) width = vb[2];
            if (!height) height = vb[3];
          }
        }

        width = width || 300;
        height = height || 150;

        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Failed to get canvas context'));

        const img = new Image();
        img.onload = () => {
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, width!, height!);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
          // fallback to svgAsPngUri
          svgAsPngUri(svgElement, {backgroundColor: 'white', scale}, (uri: string) => {
            if (uri) resolve(uri);
            else reject(new Error('Failed to convert SVG to PNG'));
          });
        };
        // Serialize SVG for image source
        const svgBlob = new Blob([svgElement.outerHTML], {type: 'image/svg+xml;charset=utf-8'});
        img.src = URL.createObjectURL(svgBlob);

      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Returns the path to the SVG file for the functional unit sld.
   */
  public getSldImagePath(sldFileName: string): string {
    let pathSld: string;
    pathSld = './assets/sld/' + sldFileName;
    return pathSld;
  }

  /**
   * Returns the path to the SVG file for the dimension layout.
   */
  public getLayoutImagePath(layoutFileName: string): string {
    let pathLayout: string;
    pathLayout = './assets/layout/' + layoutFileName;
    return pathLayout;
  }


  public async convertTechnicalOfferDrawings(components: SwitchBoardComponent[]): Promise<{
    slds: string[],
    layouts: string[]
  }> {
    const slds: string[] = [];
    const layouts: string[] = [];

    for (const component of components || []) {
      // Dimension Layouts
      if (component?.type !== ComponentType.TRANSFORMER) {
        if (component?.dimensions) {
          const componentLayouts: Layout[] = component.dimensions.layout;
          for (const layout of componentLayouts) {
            const layoutPath = this.getLayoutImagePath(layout.url);


            const layoutPngBase64 = await this.fetchAndConvertToPng(layoutPath);
            layouts.push(layoutPngBase64);
          }
        }

        // Functional Units SLDs
        for (const unit of component.functionalUnits || []) {
          if (unit?.sldKey) {
            const sldPath = this.getSldImagePath(unit.sldKey);
            const sldPngBase64 = await this.fetchAndConvertToPng(sldPath);
            slds.push(sldPngBase64);
          }
        }
      }
    }
    return {slds, layouts};
  }

}
